import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "d-flex ga-2" }
const _hoisted_2 = { class: "d-flex align-center ga-2" }
const _hoisted_3 = { class: "font-weight-medium" }
const _hoisted_4 = { class: "d-flex ga-1" }
const _hoisted_5 = { class: "text-body-1" }
const _hoisted_6 = { class: "text-caption text-grey" }
const _hoisted_7 = { class: "text-body-1" }

import { ref, computed, onMounted } from 'vue'
import { useBackupStore } from '@/store/backup'


export default /*@__PURE__*/_defineComponent({
  __name: 'BackupsTable',
  setup(__props, { expose: __expose }) {

/* eslint-disable no-undef */
const backupStore = useBackupStore()

const creatingBackup = ref(false)
const restoring = ref(false)
const deleting = ref(false)
const uploading = ref(false)
const error = ref('')
const success = ref('')

const showRestoreDialog = ref(false)
const showDeleteDialog = ref(false)
const showUploadDialog = ref(false)
const backupToRestore = ref<any>(null)
const backupToDelete = ref<any>(null)
const uploadFile = ref<File | File[] | null>(null)
const uploadError = ref('')

const headers = [
  { title: 'Filename', key: 'name', sortable: true },
  { title: 'Size', key: 'size', sortable: true },
  { title: 'Created', key: 'date', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const backups = computed(() => backupStore.getBackups)
const loading = computed(() => backupStore.isLoading)

const loadBackups = async () => {
  await backupStore.fetchBackups()
}

const createBackup = async () => {
  creatingBackup.value = true
  error.value = ''
  success.value = ''

  const result = await backupStore.createBackup()

  if (result.success) {
    success.value = result.message
  } else {
    error.value = result.message
  }

  creatingBackup.value = false
}

const downloadBackup = async (backup: any) => {
  error.value = ''
  success.value = ''

  const result = await backupStore.downloadBackup(backup)

  if (result.success) {
    success.value = result.message
  } else {
    error.value = result.message
  }
}

const openRestoreDialog = (backup: any) => {
  backupToRestore.value = backup
  showRestoreDialog.value = true
}

const closeRestoreDialog = () => {
  showRestoreDialog.value = false
  backupToRestore.value = null
}

const confirmRestore = async () => {
  if (!backupToRestore.value) return

  restoring.value = true
  error.value = ''
  success.value = ''

  const result = await backupStore.restoreBackup(backupToRestore.value.name)

  if (result.success) {
    success.value = result.message
    closeRestoreDialog()
  } else {
    error.value = result.message
  }

  restoring.value = false
}

const openDeleteDialog = (backup: any) => {
  backupToDelete.value = backup
  showDeleteDialog.value = true
}

const closeDeleteDialog = () => {
  showDeleteDialog.value = false
  backupToDelete.value = null
}

const confirmDelete = async () => {
  if (!backupToDelete.value) return

  deleting.value = true
  error.value = ''
  success.value = ''

  const result = await backupStore.deleteBackup(backupToDelete.value.name)

  if (result.success) {
    success.value = result.message
    closeDeleteDialog()
  } else {
    error.value = result.message
  }

  deleting.value = false
}

const openUploadDialog = () => {
  uploadFile.value = null
  uploadError.value = ''
  showUploadDialog.value = true
}

const closeUploadDialog = () => {
  showUploadDialog.value = false
  uploadFile.value = null
  uploadError.value = ''
}

const uploadAndRestore = async () => {
  if (!uploadFile.value) {
    uploadError.value = 'Please select a file'
    return
  }

  // Handle both single File and File[] cases
  let file: File
  if (Array.isArray(uploadFile.value)) {
    if (uploadFile.value.length === 0) {
      uploadError.value = 'Please select a file'
      return
    }
    file = uploadFile.value[0]
  } else {
    file = uploadFile.value
  }

  uploading.value = true
  uploadError.value = ''
  error.value = ''
  success.value = ''

  const result = await backupStore.uploadAndRestore(file)

  if (result.success) {
    success.value = result.message
    closeUploadDialog()
  } else {
    uploadError.value = result.message
  }

  uploading.value = false
}

const formatSize = (bytes: number) => {
  if (!bytes) return 'N/A'
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

const formatDateTime = (date: string) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleString()
}

onMounted(() => {
  loadBackups()
})

// Expose openUploadDialog for parent component
__expose({ openUploadDialog })

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_file_input = _resolveComponent("v-file-input")!

  return (_openBlock(), _createBlock(_component_v_card, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center" }, {
        default: _withCtx(() => [
          _cache[10] || (_cache[10] = _createElementVNode("span", null, "Database Backups", -1)),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_btn, {
              color: "warning",
              variant: "outlined",
              onClick: openUploadDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                    _createTextVNode("mdi-upload", -1)
                  ]))]),
                  _: 1
                }),
                _cache[7] || (_cache[7] = _createTextVNode(" Restore from File ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_btn, {
              color: "primary",
              onClick: createBackup,
              loading: creatingBackup.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode("mdi-database-plus", -1)
                  ]))]),
                  _: 1
                }),
                _cache[9] || (_cache[9] = _createTextVNode(" Create Backup ", -1))
              ]),
              _: 1
            }, 8, ["loading"])
          ])
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_text, null, {
        default: _withCtx(() => [
          (error.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 0,
                type: "error",
                variant: "tonal",
                closable: "",
                "onClick:close": _cache[0] || (_cache[0] = ($event: any) => (error.value = ''))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(error.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (success.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 1,
                type: "success",
                variant: "tonal",
                closable: "",
                "onClick:close": _cache[1] || (_cache[1] = ($event: any) => (success.value = ''))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(success.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_data_table, {
            headers: headers,
            items: backups.value,
            loading: loading.value,
            "items-per-page": 10
          }, {
            "item.name": _withCtx(({ item }) => [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_v_icon, { color: "primary" }, {
                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                    _createTextVNode("mdi-database", -1)
                  ]))]),
                  _: 1
                }),
                _createElementVNode("span", _hoisted_3, _toDisplayString((item as any).name), 1)
              ])
            ]),
            "item.size": _withCtx(({ item }) => [
              _createTextVNode(_toDisplayString(formatSize((item as any).size)), 1)
            ]),
            "item.date": _withCtx(({ item }) => [
              _createTextVNode(_toDisplayString(formatDateTime((item as any).date)), 1)
            ]),
            "item.actions": _withCtx(({ item }) => [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_v_btn, {
                  icon: "mdi-download",
                  size: "small",
                  variant: "text",
                  color: "primary",
                  onClick: ($event: any) => (downloadBackup(item as any)),
                  title: "Download Backup"
                }, null, 8, ["onClick"]),
                _createVNode(_component_v_btn, {
                  icon: "mdi-restore",
                  size: "small",
                  variant: "text",
                  color: "warning",
                  onClick: ($event: any) => (openRestoreDialog(item as any)),
                  title: "Restore from Backup"
                }, null, 8, ["onClick"]),
                _createVNode(_component_v_btn, {
                  icon: "mdi-delete",
                  size: "small",
                  variant: "text",
                  color: "error",
                  onClick: ($event: any) => (openDeleteDialog(item as any)),
                  title: "Delete Backup"
                }, null, 8, ["onClick"])
              ])
            ]),
            loading: _withCtx(() => [
              _createVNode(_component_v_skeleton_loader, { type: "table-row@5" })
            ]),
            "no-data": _withCtx(() => [
              _createVNode(_component_v_alert, {
                type: "info",
                variant: "tonal",
                class: "ma-4"
              }, {
                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                  _createTextVNode(" No backups available. Create your first backup to get started. ", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["items", "loading"])
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showRestoreDialog.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showRestoreDialog).value = $event)),
        "max-width": "500px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h5" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    color: "warning",
                    start: ""
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode("mdi-alert", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[14] || (_cache[14] = _createTextVNode(" Restore Backup ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_alert, {
                    type: "warning",
                    variant: "tonal",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                      _createElementVNode("strong", null, "Warning:", -1),
                      _createTextVNode(" Restoring from a backup will replace all current data with the backup data. This action cannot be undone. ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createElementVNode("p", _hoisted_5, [
                    _cache[16] || (_cache[16] = _createTextVNode(" Are you sure you want to restore from ", -1)),
                    _createElementVNode("strong", null, _toDisplayString(backupToRestore.value?.name), 1),
                    _cache[17] || (_cache[17] = _createTextVNode("? ", -1))
                  ]),
                  _createElementVNode("p", _hoisted_6, " Created: " + _toDisplayString(formatDateTime(backupToRestore.value?.date)), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeRestoreDialog
                  }, {
                    default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "warning",
                    onClick: confirmRestore,
                    loading: restoring.value
                  }, {
                    default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                      _createTextVNode(" Restore ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showDeleteDialog.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showDeleteDialog).value = $event)),
        "max-width": "500px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h5" }, {
                default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                  _createTextVNode(" Delete Backup ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createElementVNode("p", _hoisted_7, [
                    _cache[21] || (_cache[21] = _createTextVNode(" Are you sure you want to delete ", -1)),
                    _createElementVNode("strong", null, _toDisplayString(backupToDelete.value?.name), 1),
                    _cache[22] || (_cache[22] = _createTextVNode("? ", -1))
                  ]),
                  _cache[23] || (_cache[23] = _createElementVNode("p", { class: "text-caption text-grey" }, " This action cannot be undone. ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeDeleteDialog
                  }, {
                    default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "error",
                    onClick: confirmDelete,
                    loading: deleting.value
                  }, {
                    default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                      _createTextVNode(" Delete ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showUploadDialog.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showUploadDialog).value = $event)),
        "max-width": "600px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h5" }, {
                default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                  _createTextVNode(" Restore from Uploaded Backup ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_alert, {
                    type: "warning",
                    variant: "tonal",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                      _createElementVNode("strong", null, "Warning:", -1),
                      _createTextVNode(" This will replace all current data. Make sure you have a current backup before proceeding. ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_file_input, {
                    modelValue: uploadFile.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((uploadFile).value = $event)),
                    label: "Select backup file",
                    accept: ".json,.zip,.gz",
                    "prepend-icon": "mdi-database",
                    "show-size": "",
                    "error-messages": uploadError.value
                  }, null, 8, ["modelValue", "error-messages"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeUploadDialog
                  }, {
                    default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "warning",
                    onClick: uploadAndRestore,
                    loading: uploading.value,
                    disabled: !uploadFile.value
                  }, {
                    default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                      _createTextVNode(" Upload & Restore ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading", "disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})